import {EventListeners, MinzeElement, Reactive} from "minze";
import {SearchResultsSubscriber} from "../../modules/search/SearchResultsSubscriber";
import {IResult} from "../../modules/search";
import {Locale} from "../../modules/locale";
import { SquidexTextLabels } from "../../modules/squidex/labels";

export interface TjSearchResults {
  results: IResult;
}

Locale.getInstance().registerKeySet("fr", "searchResults", {
  "resultsTitle": {
    "plural": "résultats",
    "singular": "résultat",
  },
  "noResultsTitle": "0 résultats trouvés",
  "noResultsText": "Désolé, aucun résultat ne correspond à vos critères de recherche.",
  "groupTitle": "Par thème",
  "filtersTitle": "Filtres",
  "close": "Fermer",
});

Locale.getInstance().registerKeySet("en", "searchResults", {
  "resultsTitle": {
    "plural": "results",
    "singular": "result"
  },
  "noResultsTitle": "0 results found",
  "noResultsText": "Sorry, there were no results matching your search criteria.",
  "groupTitle": "By Theme",
  "filtersTitle": "Filters",
  "close": "Close",
});

export class TjSearchResults extends MinzeElement {
  attrs = ["data-group-id"];
  reactive: Reactive = [
    ["results", null],
    ["isDesktop", true],
    ["filtersMenuOpened", false],
    ["facetsQuantity", 0]
  ];
  searchResultsSubscriber: SearchResultsSubscriber | null = null;

  onResultUpdateCallback = (result: IResult) => {
    this.results = result
    this.rerender();
  }

  onFacetsUpdateCallback = (quantity: number) => {
    this.facetsQuantity = quantity;
    this.rerender();
  }

  renderPagination = () => {
    const renderButton = (page: number) => {
      return `
        <button class="${page === (this.results.page + 1) ? "active" : ""} pagination-button" data-page="${page}">
          ${page}
        </button>
      `
    }

    const renderEllipsis = () => `<button class="pagination-button ellipsis">...</button>`

    const totalPages = Math.ceil(this.results.total / this.results.itemsPerPage);
    const buttonArray = Array.from(
      { length: totalPages > 4 ? 5 : totalPages},
      (_, index) => index + 1
    );

    let pagination = ``

    if (totalPages > 5) {
      const currentPage = this.results.page;
      if (currentPage + 2 >= totalPages - 1) {
        pagination = pagination + renderButton(1);
        pagination = pagination + renderEllipsis();
      }
      const isCurrentPageFirst = currentPage === 0;
      const isCurrentPageLast = currentPage + 1 === totalPages;
      for (let i = 1; i <= 3; i++) {
        const page = currentPage - (
          isCurrentPageLast ? 2 :
            !isCurrentPageFirst ? 1 : 0
        ) + i;
        if (page <= totalPages) pagination = pagination + renderButton(page);
      }
      if (currentPage + 2 < totalPages) {
        if (currentPage + 2 < totalPages - 1) pagination = pagination + renderEllipsis();
        pagination = pagination + renderButton(totalPages);
      }
    } else {
      pagination = buttonArray.map((button) => `${renderButton(button)}`).join("")
    }
    return `
      <div class="pagination">
        <button class="prev pagination-button"><tj-pagination-prev-icon></tj-pagination-prev-icon></button>
        ${pagination}
        <button class="next pagination-button"><tj-pagination-next-icon></tj-pagination-next-icon></button>
      </div>
    `
  }

  changePage = (event: Event) => {
    const dateCard = (event.target as Element)?.closest('.pagination-button') as Element | null;
    this.searchResultsSubscriber?.setPage(
      parseInt(dateCard?.getAttribute("data-page") as string) - 1
    );
  }

  openFilters = () => {this.filtersMenuOpened = true}
  closeFilters = () => {this.filtersMenuOpened = false}

  onReady(): void | Promise<void> {
    const url = new URL(window.location.href);

    const search = url.searchParams.get('query');

    this.searchResultsSubscriber = new SearchResultsSubscriber(
      this.dataGroupId as string || import.meta.env.VITE_ALGOLIA_INDEX_NAME,
      this.onResultUpdateCallback.bind(this),
      this.onFacetsUpdateCallback.bind(this),
      () => this.searchResultsSubscriber?.setQuery(search || "")
    );
    this.searchResultsSubscriber.setQuery(search || "");
    const mainModule = window.TJ_MAIN_MODULE;
    mainModule.getSearchManager().subscribe(this.searchResultsSubscriber);
    const width = window.innerWidth > 0 ? window.innerWidth : screen.width;
    this.isDesktop = width > 768;

    this.rerender();

  }

  html = () => 
  /* html */ `
    ${this.filtersMenuOpened ? `
      <div class="mobile-filters-menu">
        <tj-search-facets data-group-id="${this.dataGroupId}" width="100%"></tj-search-facets>
        <tj-button class="close-filters-menu" size="small" width="100%" fontWeight="700">
          ${SquidexTextLabels.getKey("searchResults.close")}
        </tj-button>
      </div>
    ` : ''}
    <div class="search-results">
      ${this.isDesktop ? `<tj-search-facets data-group-id="${this.dataGroupId}"></tj-search-facets>` : ''}
      ${this.results ? `
        <div class="search-results-body">
          <div class="search-results-header">
            <h3 class="search-results-counter">
              ${this.results.total > 0 ?
                `${this.results.total || 0} ${SquidexTextLabels.getKey(`searchResults.${this.results.total > 1 ? "resultsTitlePlural" : "resultsTitleSingular"}`)}` :
                `${SquidexTextLabels.getKey("searchResults.noResultsTitle")}`
              }
            </h3>
            ${!this.isDesktop ? `
              <div class="filters-trigger">
                <tj-text color="#133762" font-size="16px" font-weight="500">
                  ${SquidexTextLabels.getKey("searchResults.filtersTitle")}
                  ${this.facetsQuantity ? `(${this.facetsQuantity})` : ''}
                </tj-text>
                <tj-filters-icon></tj-filters-icon>
              </div>
            ` : ''}
          </div>
          ${this.results.total > 0 ? `` : `
            <tj-box>
              <tj-text font-size="20px" color="#133762">${SquidexTextLabels.getKey("searchResults.noResultsText")}</tj-text>
            </tj-box>
          `}
          <div class="search-results-counter-list">
            ${this.results?.items ? this.results.items.map((result: any) => `
              <tj-search-result-card data="${encodeURIComponent(JSON.stringify(result))}"></tj-search-result-card>
            `).join('') : ""}
          </div>
          ${this.results.total > 0 ? `${this.renderPagination()}` : ''}
        </div>
      ` : ``}
    </div>
  `;

  css = () => `
    :host {
    }
    .search-results {
      display: flex;
      gap: 40px;
    }
    .search-results-body {
      width: 100%;
    }
    .search-results-counter {
      font-weight: 500;
      font-size: 34px;
      line-height: 125%;
      color: #133762;
      margin: 0;
    }
    .search-results-counter-list {
      display: grid;
      grid-gap: 16px;
      grid-template-columns: 1fr 1fr 1fr;
    }
    .pagination-button {
      font-weight: 700;
      font-size: 14px;
      line-height: 150%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      text-transform: uppercase;
      color: #133762;
      border-radius: 8px;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
    }
    .pagination-button:hover {
      background: rgba(6, 143, 195, 0.20);
    }
    .pagination-button.active {
      background: #068FC3;
      color: #FFF;
    }
    .pagination-button.ellipsis {
      pointer-events: none;
    }
    .pagination {
      display: flex;
      justify-content: center;
      column-gap: 2px;
    }
    tj-pagination-prev-icon, tj-pagination-next-icon {
      width: 24px;
      height: 24px;
    }
    .pagination {
      margin-top: 24px;
    }
    .filters-trigger {
      display: flex;
      gap: 6px;
    }
    .search-results-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0 0 24px;
    }
    .mobile-filters-menu {
      position: fixed;
      background: white;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 40px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      z-index: 101;
    }
    @media (max-width: 768px) {
      .search-results {
        display: block;
      }
      .search-results-counter {
        font-size: 20px;
      }
      .search-results-counter-list {
        display: flex;
        flex-direction: column;
      }
    }
  `;

  eventListeners: EventListeners = [
    [".pagination-button", "click", this.changePage],
    [".filters-trigger", "click", this.openFilters],
    [".close-filters-menu", "click", this.closeFilters],
  ];
}
